import React from 'react';
import styled from '@emotion/styled';
import { getColumnDef } from './constants';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import { JobFilter } from '../../JobFilter';
import { PaginationProps } from 'antd';
import debounce from 'lodash/debounce';

import {
  ExpandedRowContainer,
  StyledSubTable,
  StyledTable
} from './StyledComponents';
import {
  POItemColumnDef,
  SubPOItemColumnDef
} from 'app/modules/inspection/definitions/tables';
import { ToggleCollapseExpand } from 'app/modules/ui';

const Container = styled.div`
  margin-top: 23px;
  border-radius: 4px;
  box-shadow: 0px 1px 6px 0px #a5b2c833;
  box-shadow: 0px 2px 4px 1px #a5b2c81a;
  background: #fff;
  padding: 24px;
  tbody .ant-table-cell {
    cursor: pointer;
  }
`;

const StyledTotalLabel = styled.p`
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.005em;
  text-align: left;
  color: #8991a0;
`;

type Props = {
  keywords?: string;
  pagination?: PaginationProps;
  data: any[];
  loading?: boolean;
  onChange: (
    filter: { [key: string]: any },
    sort: {
      [key: string]: any;
    },
    pagination: PaginationProps
  ) => void;
  filtered?: {
    status: string[];
    result: string[];
    typeOfService: string[];
  };
  sorter?: {
    value: string;
    direction: 'desc' | 'asc';
    createdAtFrom: string;
    createdAtTo: string;
  };
};

export const Job = (props: Partial<Props>) => {
  const {
    data,
    pagination,
    loading,
    onChange,
    filtered: filteredFromProps,
    sorter: sortedFromProps
  } = props;
  const [hoveredRowKey, setHoveredRowKey] = React.useState<string | null>(null);
  const [filtered, setFiltered] = React.useState({
    status: [],
    result: [],
    typeOfService: [],
    ...filteredFromProps
  });

  const [sorter, setSorter] = React.useState({
    value: 'inspectionDate',
    direction: 'desc',
    ...sortedFromProps
  });
  const navigate = useNavigate();

  const onPaginationChange = (pagination: PaginationProps) => {
    onChange(filtered, sorter, pagination);
  };

  const onChangeSort = (sorter: {
    value: string;
    direction: 'desc' | 'asc';
  }) => {
    setSorter(sorter as any);
    onChange(filtered, sorter, pagination);
  };
  const onChangeFilter = (filter: Record<string, string[]>) => {
    setFiltered(filter as any);
    onChange(filter, sorter, {
      ...pagination,
      current: 1
    });
  };

  // Debounced function to set hovered row key
  const debouncedSetHoveredRowKey = React.useMemo(
    () =>
      debounce((rowKey: string | null) => {
        setHoveredRowKey(rowKey);
      }, 50), // 200ms debounce delay
    []
  );

  const handleMouseEnter = (rowKey: string) => {
    debouncedSetHoveredRowKey(rowKey); // Call debounced function on mouse enter
  };

  const handleMouseLeave = () => {
    debouncedSetHoveredRowKey(null); // Call debounced function on mouse leave
  };

  React.useEffect(() => {
    return () => {
      debouncedSetHoveredRowKey.cancel(); // Cleanup debounce on unmount
    };
  }, [debouncedSetHoveredRowKey]);

  return (
    <div>
      <JobFilter
        onChangeFilter={onChangeFilter}
        onChangeSort={onChangeSort}
        filtered={filtered}
        sorter={sorter as any}
      ></JobFilter>
      <Container>
        <StyledTable
          loading={loading}
          scroll={{ x: '80vw' }}
          rowKey={(record) => record.id}
          dataSource={data}
          columns={getColumnDef()}
          onChange={onPaginationChange}
          pagination={{
            ...pagination,
            current: pagination.current,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => {
              return (
                <StyledTotalLabel>
                  {range[0]}-{range[1]} of {total}
                </StyledTotalLabel>
              );
            }
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                navigate(`job/${record.id}`);
              }
            };
          }}
          expandable={{
            expandedRowRender: (record) => {
              return (
                <ExpandedRowContainer>
                  <StyledSubTable
                    pagination={false}
                    columns={POItemColumnDef()}
                    dataSource={
                      record?.inspectionPOReportGroups[0]?.inspectionPOItems ??
                      []
                    }
                    onRow={(poItem) => {
                      return {
                        className: poItem.id === hoveredRowKey ? 'hovered' : '',
                        onClick: () => {
                          navigate(`job/${record.id}`);
                        },
                        onMouseEnter: () => {
                          handleMouseEnter(poItem.id);
                        },
                        onMouseLeave: () => {
                          handleMouseLeave();
                        }
                      };
                    }}
                    rowKey={(poItemRecord) => poItemRecord.id}
                    expandable={{
                      expandedRowRender: (poItemRecord) => {
                        const subRecords = poItemRecord.subPoItems || [];
                        return (
                          <ExpandedRowContainer
                            style={{ marginLeft: 90 }}
                            onMouseEnter={() => {
                              handleMouseEnter(poItemRecord.id);
                            }}
                            onMouseLeave={() => {
                              handleMouseLeave();
                            }}
                          >
                            <StyledSubTable
                              className={`child ${
                                poItemRecord.id === hoveredRowKey
                                  ? 'hovered'
                                  : ''
                              }`}
                              pagination={false}
                              columns={SubPOItemColumnDef()}
                              dataSource={subRecords}
                              onRow={() => {
                                return {
                                  onClick: () => {
                                    navigate(`job/${record.id}`);
                                  }
                                };
                              }}
                            />
                          </ExpandedRowContainer>
                        );
                      },
                      rowExpandable: () => true,
                      expandIcon: ({
                        expanded,
                        record: poItemRecord,
                        onExpand
                      }) => {
                        return (
                          <ToggleCollapseExpand
                            isExpanded={expanded}
                            onToggle={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onExpand(poItemRecord, e);
                            }}
                          />
                        );
                      }
                    }}
                  ></StyledSubTable>
                </ExpandedRowContainer>
              );
            },
            expandIcon: ({ expanded, record, onExpand }) => {
              if (expanded) {
                return (
                  <UpOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                  />
                );
              }
              return (
                <DownOutlined
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpand(record, e);
                  }}
                />
              );
            }
          }}
        ></StyledTable>
      </Container>
    </div>
  );
};
